import {getTestProps} from '@benchling/js-test-utils';
import {Anchor, Box, Group, Stack, Text, createStyles} from '@mantine/core';
import React, {useCallback, useEffect, useReducer, useRef} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {redirectTo, setLocationHref} from 'BenchlingUtil';
import {MODEL_HUB_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import navigateToURL from 'lib/navigateToURL';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {
  HOME_URL,
  getGoogleOauthBeginUrl,
  getSigninUrl,
  getSignupUrl,
  type AuthMethod,
  type EmailType,
  type Screen,
  SIGNUP_TAGLINE,
} from './constants';
import {
  cancelGoogleOauthSignup as apiCancelGoogleOauthSignup,
  getSignupErrorMessage,
  isSignupApiError,
  prepareGoogleOauthSignup as apiPrepareGoogleOauthSignup,
  resendCode as apiResendCode,
  sendCode as apiSendCode,
  signup as apiSignup,
  verifyCode as apiVerifyCode,
} from './signupApi';
import SignupProgressList from './SignupProgressList';
import {AI_GRADIENT, SIGNUP_CARD_MAX_WIDTH_PX, SIGNUP_STEP_STACK_SPACING} from './signupUi';
import EmailStep from './steps/EmailStep';
import ProfileStep from './steps/ProfileStep';
import SuccessStep from './steps/SuccessStep';
import VerifyStep from './steps/VerifyStep';
import WorkStep from './steps/WorkStep';

export interface SurveyAnswers {
  department: string | null;
  workFocus: Array<string>;
}

interface SignupState {
  authMethod: AuthMethod;
  screen: Screen;
  email: string;
  emailType: EmailType;
  code: string;
  firstName: string;
  lastName: string;
  password: string;
  tosAcceptedAt: string | null;
  survey: SurveyAnswers;
  loading: boolean;
  error: string | null;
}

const INITIAL_STATE: SignupState = {
  authMethod: 'email',
  screen: 'email',
  email: '',
  emailType: 'personal',
  code: '',
  firstName: '',
  lastName: '',
  password: '',
  tosAcceptedAt: null,
  survey: {department: null, workFocus: []},
  loading: false,
  error: null,
};

function getInitialState(): SignupState {
  const oauthSignup = window.bootstrap?.modelHubSignup?.oauthSignup;
  if (oauthSignup == null) {
    return INITIAL_STATE;
  }
  const hasTermsConsent = oauthSignup.hasTermsConsent === true;
  return {
    ...INITIAL_STATE,
    authMethod: 'google',
    screen: hasTermsConsent ? 'profile' : 'email',
    email: oauthSignup.email,
    emailType: oauthSignup.emailType,
    firstName: oauthSignup.firstName,
    lastName: oauthSignup.lastName,
    tosAcceptedAt: null,
  };
}

type SignupAction =
  | {type: 'setField'; field: 'email' | 'code' | 'firstName' | 'lastName' | 'password'; value: string}
  | {type: 'setTosAcceptedAt'; value: string | null}
  | {type: 'setSurvey'; value: Partial<SurveyAnswers>}
  | {type: 'goToScreen'; screen: Screen}
  | {type: 'backToVerify'}
  | {type: 'resetToEmail'}
  | {type: 'googleOauthCanceled'}
  | {type: 'requestStart'}
  | {type: 'requestError'; error: string}
  | {type: 'codeSent'; emailType: EmailType}
  | {type: 'codeResent'}
  | {type: 'codeVerified'; emailType: EmailType}
  | {type: 'accountCreated'};

function reducer(state: SignupState, action: SignupAction): SignupState {
  switch (action.type) {
    case 'setField':
      return {...state, [action.field]: action.value};
    case 'setTosAcceptedAt':
      return {...state, tosAcceptedAt: action.value};
    case 'setSurvey':
      return {...state, survey: {...state.survey, ...action.value}};
    case 'goToScreen':
      return {...state, screen: action.screen, error: null};
    case 'backToVerify':
      // Clear the code so the PinInput doesn't immediately re-fire onComplete on re-mount.
      return {...state, screen: 'verify', code: '', error: null};
    case 'resetToEmail':
      return {...state, authMethod: 'email', screen: 'email', code: '', error: null};
    case 'googleOauthCanceled':
      return INITIAL_STATE;
    case 'requestStart':
      return {...state, loading: true, error: null};
    case 'requestError':
      return {...state, loading: false, error: action.error};
    case 'codeSent':
      return {...state, loading: false, error: null, emailType: action.emailType, screen: 'verify'};
    case 'codeResent':
      // Already on the verify screen; just clear the in-flight state so the Verify button and
      // Resend link re-enable (both gate on !loading).
      return {...state, loading: false, error: null};
    case 'codeVerified':
      return {...state, loading: false, error: null, emailType: action.emailType, screen: 'profile'};
    case 'accountCreated':
      return {...state, loading: false, error: null, screen: 'success'};
    default:
      return state;
  }
}

function activeStep(screen: Screen): number {
  switch (screen) {
    case 'email':
    case 'verify':
      return 0;
    case 'profile':
      return 1;
    case 'work':
    case 'success':
      return 2;
    default:
      return 0;
  }
}

function isPlainLeftClick(event: React.MouseEvent<HTMLAnchorElement>): boolean {
  return (
    event.button === 0 &&
    !event.metaKey &&
    !event.altKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.defaultPrevented
  );
}

export default function ModelHubSignup(): JSX.Element {
  const {classes} = useStyles();
  const [state, dispatch] = useReducer(reducer, getInitialState());
  const modelHubSignupBootstrap = window.bootstrap?.modelHubSignup;
  const nextURL = modelHubSignupBootstrap?.nextURL ?? null;
  const signinURL = getSigninUrl(nextURL);
  const signupURL = getSignupUrl(nextURL);
  const googleOauthBeginURL = getGoogleOauthBeginUrl(nextURL);
  const postSignupRedirectURL = nextURL ?? HOME_URL;
  const showGoogleSignup = modelHubSignupBootstrap?.showGoogleSignup === true;
  const initialGoogleSignupRef = useRef(state.authMethod === 'google' ? {emailType: state.emailType} : null);

  useEffect(() => {
    trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.SIGNUP_VIEWED);
    if (initialGoogleSignupRef.current != null) {
      trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.SIGNUP_GOOGLE_RETURNED, initialGoogleSignupRef.current);
    }
  }, []);

  const handleLoginClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>): void => {
      if (!isPlainLeftClick(event)) {
        return;
      }
      event.preventDefault();
      trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.SIGNUP_EXITED_TO_SIGN_IN);
      setLocationHref(signinURL);
    },
    [signinURL]
  );

  const handleSendCode = useCallback(async () => {
    dispatch({type: 'requestStart'});
    try {
      const response = await apiSendCode(state.email, state.tosAcceptedAt);
      trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.SIGNUP_EMAIL_SUBMITTED, {emailType: response.email_type});
      dispatch({type: 'codeSent', emailType: response.email_type ?? 'personal'});
    } catch (error) {
      if (!isSignupApiError(error)) {
        throw error;
      }
      trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.SIGNUP_EMAIL_SUBMIT_FAILED);
      dispatch({type: 'requestError', error: getSignupErrorMessage(error)});
    }
  }, [state.email, state.tosAcceptedAt]);

  const handleGoogleSignup = useCallback(async () => {
    dispatch({type: 'requestStart'});
    try {
      await apiPrepareGoogleOauthSignup(state.tosAcceptedAt);
      trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.SIGNUP_GOOGLE_INITIATED);
      setLocationHref(googleOauthBeginURL);
    } catch (error) {
      if (!isSignupApiError(error)) {
        throw error;
      }
      dispatch({type: 'requestError', error: getSignupErrorMessage(error)});
    }
  }, [googleOauthBeginURL, state.tosAcceptedAt]);

  const handleCancelGoogleOauthSignup = useCallback((): void => {
    if (state.authMethod !== 'google') {
      dispatch({type: 'resetToEmail'});
      return;
    }
    trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.SIGNUP_GOOGLE_CANCELED);
    dispatch({type: 'requestStart'});
    apiCancelGoogleOauthSignup()
      .then(() => {
        dispatch({type: 'googleOauthCanceled'});
      })
      .catch((error: unknown) => {
        if (!isSignupApiError(error)) {
          throw error;
        }
        dispatch({type: 'requestError', error: getSignupErrorMessage(error)});
      });
  }, [state.authMethod]);

  const handleUseEmailInstead = useCallback((): void => {
    trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.SIGNUP_GOOGLE_CANCELED);
    dispatch({type: 'requestStart'});
    apiCancelGoogleOauthSignup()
      .then(() => {
        setLocationHref(signupURL);
      })
      .catch((error: unknown) => {
        if (!isSignupApiError(error)) {
          throw error;
        }
        dispatch({type: 'requestError', error: getSignupErrorMessage(error)});
      });
  }, [signupURL]);

  const handleVerifyCode = useCallback(async () => {
    trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.SIGNUP_CODE_VERIFICATION_STARTED);
    dispatch({type: 'requestStart'});
    try {
      const response = await apiVerifyCode(state.email, state.code);
      trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.SIGNUP_CODE_VERIFIED, {emailType: response.email_type});
      dispatch({type: 'codeVerified', emailType: response.email_type ?? state.emailType});
    } catch (error) {
      if (!isSignupApiError(error)) {
        throw error;
      }
      trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.SIGNUP_CODE_VERIFICATION_FAILED);
      dispatch({type: 'requestError', error: getSignupErrorMessage(error)});
    }
  }, [state.email, state.code, state.emailType]);

  const handleResendCode = useCallback(async () => {
    dispatch({type: 'requestStart'});
    try {
      await apiResendCode(state.email);
      trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.SIGNUP_CODE_RESENT);
      dispatch({type: 'codeResent'});
    } catch (error) {
      if (!isSignupApiError(error)) {
        throw error;
      }
      dispatch({type: 'requestError', error: getSignupErrorMessage(error)});
    }
  }, [state.email]);

  const handleCreateAccount = useCallback(async () => {
    // Survey answers are analytics-only — fired here, never persisted server-side.
    trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.SIGNUP_SURVEY_SUBMITTED, {
      authMethod: state.authMethod,
      emailType: state.emailType,
      department: state.survey.department,
      workFocus: state.survey.workFocus,
    });
    dispatch({type: 'requestStart'});
    try {
      await apiSignup({
        authMethod: state.authMethod,
        email: state.email,
        code: state.authMethod === 'email' ? state.code : undefined,
        firstName: state.firstName,
        lastName: state.lastName,
        password: state.authMethod === 'email' ? state.password : undefined,
      });
      trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.SIGNUP_COMPLETED, {
        authMethod: state.authMethod,
        emailType: state.emailType,
      });
      dispatch({type: 'accountCreated'});
    } catch (error) {
      if (!isSignupApiError(error)) {
        throw error;
      }
      dispatch({type: 'requestError', error: getSignupErrorMessage(error)});
    }
  }, [
    state.email,
    state.authMethod,
    state.code,
    state.firstName,
    state.lastName,
    state.password,
    state.emailType,
    state.survey,
  ]);

  const renderScreen = (): JSX.Element | null => {
    switch (state.screen) {
      case 'email':
        return (
          <EmailStep
            email={state.email}
            error={state.error}
            googleOnly={state.authMethod === 'google'}
            loading={state.loading}
            showGoogleSignup={showGoogleSignup}
            tosAccepted={state.tosAcceptedAt != null}
            onBack={() => {
              trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.SIGNUP_EXITED_TO_HOME);
              navigateToURL(HOME_URL);
            }}
            onEmailChange={(value) => {
              dispatch({type: 'setField', field: 'email', value});
            }}
            onGoogleSignup={handleGoogleSignup}
            onSubmit={handleSendCode}
            onTosAcceptedChange={(value) => {
              dispatch({
                type: 'setTosAcceptedAt',
                value: value ? new Date().toISOString() : null,
              });
            }}
            onUseEmailInstead={state.authMethod === 'google' ? handleUseEmailInstead : undefined}
          />
        );
      case 'verify':
        return (
          <VerifyStep
            code={state.code}
            email={state.email}
            error={state.error}
            loading={state.loading}
            onBack={() => {
              dispatch({type: 'resetToEmail'});
            }}
            onCodeChange={(value) => {
              dispatch({type: 'setField', field: 'code', value});
            }}
            onResendCode={handleResendCode}
            onSubmit={handleVerifyCode}
          />
        );
      case 'profile':
        return (
          <ProfileStep
            authMethod={state.authMethod}
            department={state.survey.department}
            email={state.email}
            emailType={state.emailType}
            error={state.error}
            firstName={state.firstName}
            lastName={state.lastName}
            password={state.password}
            onBack={() => {
              if (state.authMethod === 'google') {
                handleCancelGoogleOauthSignup();
                return;
              }
              dispatch({type: 'backToVerify'});
            }}
            onDepartmentChange={(value) => {
              dispatch({type: 'setSurvey', value: {department: value}});
            }}
            onFirstNameChange={(value) => {
              dispatch({type: 'setField', field: 'firstName', value});
            }}
            onLastNameChange={(value) => {
              dispatch({type: 'setField', field: 'lastName', value});
            }}
            onPasswordChange={(value) => {
              dispatch({type: 'setField', field: 'password', value});
            }}
            onSubmit={() => {
              trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.SIGNUP_PROFILE_CONTINUE_CLICKED);
              dispatch({type: 'goToScreen', screen: 'work'});
            }}
            onUseWorkEmail={() => {
              handleCancelGoogleOauthSignup();
            }}
          />
        );
      case 'work':
        return (
          <WorkStep
            error={state.error}
            loading={state.loading}
            survey={state.survey}
            onBack={() => {
              trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.SIGNUP_SURVEY_SKIPPED_OR_BACK_CLICKED);
              dispatch({type: 'goToScreen', screen: 'profile'});
            }}
            onSubmit={handleCreateAccount}
            onSurveyChange={(value) => {
              dispatch({type: 'setSurvey', value});
            }}
          />
        );
      case 'success':
        return (
          <SuccessStep
            emailType={state.emailType}
            firstName={state.firstName}
            // Signup established a session server-side. A full-page load (not client-side
            // navigateToURL) is required so the server re-renders and bootstraps the signed-in
            // app; a Backbone navigation would leave the SPA showing signed-out until a refresh.
            onGoToModelHub={() => {
              redirectTo(postSignupRedirectURL);
            }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Group
      align="stretch"
      className={classes.shell}
      noWrap={true}
      spacing={0}
      {...getTestProps({component: 'ModelHubSignup', element: 'root'})}
    >
      <Box className={classes.aside}>
        <Stack className={classes.asideInner} justify="space-between">
          <Stack className={classes.asideTop} spacing="xl">
            <Group spacing="xs" noWrap={true}>
              <BenchlingIcon className={classes.brandIcon} icon="benchling-logo" />
              <div>
                <Text className={classes.brandName}>Benchling</Text>
                <Text className={classes.brandProduct}>Model Hub</Text>
              </div>
            </Group>
            <Text className={classes.asideHeading}>{SIGNUP_TAGLINE}</Text>
          </Stack>
          <Box className={classes.asideStepper}>
            <SignupProgressList
              activeStepIndex={activeStep(state.screen)}
              allStepsComplete={state.screen === 'success'}
            />
          </Box>
        </Stack>
      </Box>
      <Box className={classes.main}>
        {state.screen !== 'email' && state.screen !== 'success' && (
          <Group
            className={classes.topBar}
            position="right"
            spacing={4}
            {...getTestProps({component: 'ModelHubSignup', element: 'topLoginPrompt'})}
          >
            <Text className={classes.accountPrompt}>Already have an account?</Text>
            <Anchor className={classes.loginLink} href={signinURL} size="md" onClick={handleLoginClick}>
              Log in
            </Anchor>
          </Group>
        )}
        <Box className={classes.canvas}>
          <Box className={classes.card}>
            {renderScreen()}
            {state.screen === 'email' && (
              <Group
                className={classes.inlineAccountPrompt}
                position="center"
                spacing={4}
                {...getTestProps({component: 'ModelHubSignup', element: 'inlineLoginPrompt'})}
              >
                <Text className={classes.accountPrompt}>Already have an account?</Text>
                <Anchor className={classes.loginLink} href={signinURL} size="md" onClick={handleLoginClick}>
                  Log in
                </Anchor>
              </Group>
            )}
          </Box>
        </Box>
      </Box>
    </Group>
  );
}

const useStyles = createStyles((theme) => ({
  shell: {
    backgroundColor: theme.white,
    minHeight: '100vh',
  },
  aside: {
    background: AI_GRADIENT,
    display: 'flex',
    flex: '0 0 clamp(280px, 32vw, 460px)',
    flexDirection: 'column',
    minHeight: '100vh',
    padding: '40px clamp(24px, 3vw, 44px)',
  },
  asideInner: {
    flex: 1,
    minHeight: '100%',
  },
  asideTop: {
    paddingTop: theme.spacing.sm,
  },
  asideStepper: {
    paddingBottom: theme.spacing.lg,
    paddingTop: theme.spacing.xxl,
  },
  brandIcon: {
    color: theme.white,
    fontSize: 24,
  },
  brandName: {
    color: theme.white,
    fontWeight: 700,
    lineHeight: 1.2,
  },
  brandProduct: {
    color: theme.white,
    fontSize: theme.fontSizes.sm,
    lineHeight: 1.2,
  },
  asideHeading: {
    color: theme.white,
    fontSize: 22,
    fontWeight: 700,
    lineHeight: 1.3,
  },
  accountPrompt: {
    color: theme.colors.gray[7],
    fontSize: theme.fontSizes.md,
  },
  inlineAccountPrompt: {
    marginTop: SIGNUP_STEP_STACK_SPACING,
  },
  loginLink: {
    fontWeight: 600,
  },
  main: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    minWidth: 0,
  },
  topBar: {
    padding: theme.spacing.lg,
  },
  canvas: {
    alignItems: 'center',
    display: 'flex',
    flex: 1,
    justifyContent: 'center',
    padding: theme.spacing.xl,
    // Pull the form up slightly so it reads as vertically centered despite the top bar.
    paddingBottom: 80,
  },
  card: {
    maxWidth: SIGNUP_CARD_MAX_WIDTH_PX,
    width: '100%',
  },
}));
